import { useCallback, useEffect, useRef, useState } from 'react';
import { mensagemDoErro } from '../../api/erros.js';
import { dispararCargaDeLotes, obterCargaDeLotes } from '../../api/lotes.js';
import type { CargaDeLotes as EstadoDaCarga } from '../../api/lotes.js';
import { useConsulta } from '../../api/useConsulta.js';
import { AvisoDaTela } from '../../componentes/AvisoDaTela.js';
import { Botao } from '../../componentes/Botao.js';
import { ChipStatus } from '../../componentes/ChipStatus.js';
import type { TomStatus } from '../../componentes/ChipStatus.js';
import { formatarDataHora } from '../../componentes/datas.js';

/** De quanto em quanto tempo a tela pergunta de novo enquanto a carga roda. */
export const INTERVALO_DE_ACOMPANHAMENTO_MS = 5_000;

const SITUACAO: Readonly<
  Record<EstadoDaCarga['status'], { readonly tom: TomStatus; readonly rotulo: string }>
> = {
  NaoIniciada: { tom: 'desativado', rotulo: 'Não iniciada' },
  EmAndamento: { tom: 'andamento', rotulo: 'Em andamento' },
  Concluida: { tom: 'concluida', rotulo: 'Concluída' },
  Falhou: { tom: 'falha', rotulo: 'Falhou' },
};

function detalheDaCarga(carga: EstadoDaCarga): string | null {
  if (carga.status === 'Falhou') {
    return carga.ultimoErro;
  }

  if (carga.status === 'NaoIniciada') {
    return null;
  }

  const partes = [
    `${String(carga.lotesProcessados)} lotes lidos`,
    carga.concluidaEm !== null ? `concluída em ${formatarDataHora(carga.concluidaEm)}` : null,
  ].filter((parte): parte is string => parte !== null);

  return partes.join(', ');
}

type Props = {
  /** A carga terminou: quem chama pergunta de novo se já há lotes. */
  readonly aoConcluir: () => void;
};

/**
 * Carga de lotes do ERP, para o Administrador destravar a identificação por
 * lote. Mostra o estado da última carga e, enquanto ela roda, pergunta de
 * novo a cada poucos segundos — o botão some nesse meio tempo, para não
 * disparar duas vezes.
 */
export function CargaDeLotes({ aoConcluir }: Props) {
  const consulta = useConsulta(obterCargaDeLotes);
  const [disparada, definirDisparada] = useState<EstadoDaCarga | null>(null);
  const [disparando, definirDisparando] = useState(false);
  const [falha, definirFalha] = useState<string | null>(null);
  const concluiu = useRef(aoConcluir);
  concluiu.current = aoConcluir;

  const { recarregar } = consulta;
  const carga = disparada ?? consulta.dados;
  const emAndamento = carga?.status === 'EmAndamento';

  useEffect(() => {
    if (!emAndamento) {
      return;
    }

    const relogio = setTimeout(() => {
      definirDisparada(null);
      recarregar();
    }, INTERVALO_DE_ACOMPANHAMENTO_MS);

    return () => {
      clearTimeout(relogio);
    };
  }, [emAndamento, recarregar, carga]);

  const anterior = useRef<EstadoDaCarga['status'] | null>(null);
  useEffect(() => {
    const atual = carga?.status ?? null;
    if (atual === 'Concluida' && anterior.current !== null && anterior.current !== 'Concluida') {
      concluiu.current();
    }
    anterior.current = atual;
  }, [carga]);

  const disparar = useCallback(() => {
    definirDisparando(true);
    definirFalha(null);

    dispararCargaDeLotes()
      .then((estado) => {
        definirDisparando(false);
        definirDisparada(estado);
      })
      .catch((erro: unknown) => {
        definirDisparando(false);
        definirFalha(mensagemDoErro(erro));
      });
  }, []);

  const situacao = carga === null ? null : SITUACAO[carga.status];
  const detalhe = carga === null ? null : detalheDaCarga(carga);

  return (
    <div className="configuracoes__carga">
      <p className="configuracoes__estado">
        <span className="configuracoes__detalhe">Carga de lotes do ERP:</span>
        {situacao !== null ? (
          <ChipStatus tom={situacao.tom} rotulo={situacao.rotulo} />
        ) : (
          <span className="configuracoes__detalhe" role="status">
            {consulta.erro ?? 'Consultando…'}
          </span>
        )}
        {detalhe !== null && <span className="configuracoes__detalhe">{detalhe}</span>}
      </p>

      {falha !== null && <AvisoDaTela tom="erro">{falha}</AvisoDaTela>}

      {!emAndamento && (
        <div className="configuracoes__acoes">
          <Botao onClick={disparar} disabled={disparando}>
            {disparando ? 'Disparando…' : 'Carregar lotes do ERP'}
          </Botao>
        </div>
      )}
    </div>
  );
}
